import { type ChangeEventHandler, useEffect, useState } from 'react';
import { getType } from '../../../../sources/type';
import { getValue, getValueList } from '../../../../sources/value';
import type { OperationAPI, OperationSortDirection } from '../../../../types/operation';
import type { TypeElementPropertyAPI } from '../../../../types/type';
import type { ContentType, ValueElementIdReferenceAPI } from '../../../../types/value';
import { getByID } from '../../../../utils/collection';
import { isNullOrEmpty } from '../../../../utils/guard';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import FormGroup from '../../../generic/FormGroup';
import ModalBody from '../../../generic/modal/ModalBody';
import ModalFooter from '../../../generic/modal/ModalFooter';
import Loader from '../../../loader/Loader';
import { useNotificationDispatch } from '../../../notifications/NotificationProvider';
import ValueSelectorModal from '../../../values/selector/ValueSelectorModal';
import screens from '../common/screens';
import { useMapElement } from '../contextProviders/MapElementProvider';
import { OPERATION_COMMANDS, RELATIVE_DATE_UNITS, RELATIVE_DATETIME_UNITS } from './constants';
import { useOperator } from './OperatorProvider';
import { createOperationAPI } from './utils';

export const getVisibleApplyCommands = (valueToApply: ValueElementIdReferenceAPI | null) => {
    if (valueToApply === null) {
        return [];
    }

    const applyContentType = valueToApply?.contentType ?? '';
    const nonFlaggedDateCommands = [
        OPERATION_COMMANDS.setEqual,
        OPERATION_COMMANDS.empty,
        OPERATION_COMMANDS.new,
    ];
    switch (applyContentType) {
        case 'ContentString':
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.join,
            ];

        case 'ContentNumber':
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.add,
                OPERATION_COMMANDS.subtract,
                OPERATION_COMMANDS.divide,
                OPERATION_COMMANDS.multiply,
                OPERATION_COMMANDS.modulo,
            ];

        case 'ContentList':
            return [
                OPERATION_COMMANDS.setEqual,
                OPERATION_COMMANDS.empty,
                OPERATION_COMMANDS.new,
                OPERATION_COMMANDS.update,
                OPERATION_COMMANDS.remove,
                OPERATION_COMMANDS.sort,
            ];

        case 'ContentDateTime':
        case 'ContentDate':
            return [...nonFlaggedDateCommands, OPERATION_COMMANDS.setRelativeDate];
        default:
            return [OPERATION_COMMANDS.setEqual, OPERATION_COMMANDS.empty, OPERATION_COMMANDS.new];
    }
};

export const getVisibleReferenceCommands = (
    valueToApply: ValueElementIdReferenceAPI | null,
    showReferenceCommandSelector?: boolean,
) => {
    if (valueToApply === null || showReferenceCommandSelector === false) {
        return [];
    }

    const applyContentType = valueToApply?.contentType ?? '';

    switch (applyContentType) {
        case 'ContentNumber':
            return [OPERATION_COMMANDS.value_of, OPERATION_COMMANDS.getLength];

        case 'ContentObject':
            return [
                OPERATION_COMMANDS.value_of,
                OPERATION_COMMANDS.getFirst,
                OPERATION_COMMANDS.getLast,
                OPERATION_COMMANDS.getNext,
            ];
        default:
            return [OPERATION_COMMANDS.value_of];
    }
};

export const getValidReferenceContentType = (
    valueToApply: ValueElementIdReferenceAPI | null,
    valueToApplyCommand: string | null,
    valueToReferenceCommand: string | null,
    showReferenceValueSelector?: boolean,
) => {
    if (valueToApply === null || showReferenceValueSelector === false) {
        return null;
    }

    const applyContentType = valueToApply?.contentType ?? null;

    switch (applyContentType) {
        case 'ContentNumber':
            return valueToReferenceCommand === OPERATION_COMMANDS.getLength.command
                ? 'ContentList'
                : 'ContentNumber';

        case 'ContentObject':
            return valueToReferenceCommand === OPERATION_COMMANDS.getFirst.command ||
                valueToReferenceCommand === OPERATION_COMMANDS.getLast.command ||
                valueToReferenceCommand === OPERATION_COMMANDS.getNext.command
                ? 'ContentList'
                : 'ContentObject';

        case 'ContentList':
            return valueToApplyCommand === OPERATION_COMMANDS.update.command ||
                valueToApplyCommand === OPERATION_COMMANDS.remove.command
                ? 'ContentObject'
                : 'ContentList';
        default:
            return applyContentType;
    }
};

export const getVisibleReferenceRelativeCommands = (
    showReferenceRelativeCommandSelector: boolean,
) => {
    if (showReferenceRelativeCommandSelector === false) {
        return [];
    }

    return [OPERATION_COMMANDS.add, OPERATION_COMMANDS.subtract];
};

const OperationDetails = () => {
    const { mapElement, onSwitchScreen, setMapElement, setConfigTitle, container } =
        useMapElement();

    const dispatchNotification = useNotificationDispatch();

    const { selectedOperationIndex, setSelectedOperationIndex } = useOperator();

    const operation =
        typeof selectedOperationIndex === 'number'
            ? mapElement.operations?.[selectedOperationIndex]
            : null;

    /**
     * State values
     */

    const [isLoadingValues, setIsLoadingValues] = useState(operation !== null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    // Value to apply state
    // The value object that the operation operates on
    const [valueToApply, setValueToApply] = useState<ValueElementIdReferenceAPI | null>(null);
    // The command (string) that will be applied to the value
    const [valueToApplyCommand, setValueToApplyCommand] = useState<string | null>(
        operation?.valueElementToApplyId?.command ?? null,
    );

    // Value to reference state
    // The reference value object that's used to operate
    const [valueToReference, setValueToReference] = useState<ValueElementIdReferenceAPI | null>(
        null,
    );
    // The command (string) that will be used to retrieve a value
    const [valueToReferenceCommand, setValueToReferenceCommand] = useState<string | null>(
        operation?.valueElementToReferenceId?.command ?? null,
    );

    // Relative date operation state
    // The value which will be applied to value to reference before storing the result in value to apply
    const [valueToReferenceRelative, setValueToReferenceRelative] =
        useState<ValueElementIdReferenceAPI | null>(null);
    // The command (string) that will be used to retrieve a value to apply to the valueToReference
    const [valueToReferenceRelativeCommand, setValueToReferenceRelativeCommand] = useState<
        string | null
    >(operation?.valueElementToReferenceRelativeId?.command ?? null);
    // The the unit which will be used to operate on the relative date
    const [valueToReferenceRelativeUnit, setValueToReferenceRelativeUnit] = useState(
        operation?.valueElementToReferenceRelativeId?.relativeUnit ?? null,
    );

    // Sorting state
    const [sortDirection, setSortDirection] = useState<OperationSortDirection>(
        operation?.valueElementToApplySortDirection ?? 'ASCENDING',
    );
    // A list of all properties of the valueToApply's type
    const [sortProperties, setSortProperties] = useState<TypeElementPropertyAPI[]>([]);
    // The property of the valueToApply to sort by
    const [selectedSortProperty, setSelectedSortProperty] = useState<TypeElementPropertyAPI | null>(
        null,
    );

    // The id of the type of the valueToReference
    const [referenceTypeId, setReferenceTypeId] = useState<string | null>(null);

    /**
     * Functions
     */

    // Checks the validity of the form
    const checkValid = () => {
        if (valueToApply === null || valueToApplyCommand === null) {
            return false;
        }

        if (
            valueToApplyCommand === OPERATION_COMMANDS.sort.command &&
            selectedSortProperty === null
        ) {
            return false;
        }

        if (
            valueToApplyCommand === OPERATION_COMMANDS.setEqual.command ||
            valueToApplyCommand === OPERATION_COMMANDS.remove.command ||
            valueToApplyCommand === OPERATION_COMMANDS.update.command ||
            valueToApplyCommand === OPERATION_COMMANDS.add.command ||
            valueToApplyCommand === OPERATION_COMMANDS.subtract.command ||
            valueToApplyCommand === OPERATION_COMMANDS.divide.command ||
            valueToApplyCommand === OPERATION_COMMANDS.multiply.command ||
            valueToApplyCommand === OPERATION_COMMANDS.join.command ||
            valueToApplyCommand === OPERATION_COMMANDS.modulo.command ||
            valueToApplyCommand === OPERATION_COMMANDS.setRelativeDate.command
        ) {
            if (valueToReference === null || valueToReferenceCommand === null) {
                return false;
            }
        }

        if (valueToApplyCommand === OPERATION_COMMANDS.setRelativeDate.command) {
            if (
                valueToReferenceRelative === null ||
                valueToReferenceRelativeCommand === null ||
                valueToReferenceRelativeUnit === null
            ) {
                return false;
            }
        }

        return true;
    };

    const onSave = () => {
        setHasSubmitted(true);
        if (checkValid()) {
            const index = selectedOperationIndex ?? mapElement.operations?.length ?? 0;
            const operations = [...(mapElement.operations ?? [])];

            const operationToSave: OperationAPI = createOperationAPI({
                order: index,
                disabled: false,
                macroElementToExecuteDeveloperName: null,
                macroElementToExecuteId: null,
                valueElementToApplyDeveloperName: valueToApply?.developerName ?? null,
                valueElementToApplyContentType: valueToApply?.contentType ?? null,
                valueElementToApplyId: {
                    command: valueToApplyCommand ?? null,
                    id: valueToApply?.id ?? null,
                    typeElementPropertyId: valueToApply?.typeElementPropertyId ?? null,
                },
                valueElementToApplyPropertyDeveloperName:
                    valueToApply?.typeElementPropertyDeveloperName ?? null,
                valueElementToApplySortById: selectedSortProperty?.id ?? null,
                valueElementToApplySortByDeveloperName: selectedSortProperty?.developerName ?? null,
                valueElementToApplySortDirection: sortDirection,
                valueElementToReferenceDeveloperName: valueToReference?.developerName ?? null,
                valueElementToReferenceContentType: valueToReference?.contentType ?? null,
                valueElementToReferenceId: null,
                valueElementToReferencePropertyDeveloperName:
                    valueToReference?.typeElementPropertyDeveloperName ?? null,
            });

            if (valueToReference) {
                operationToSave.valueElementToReferenceId = {
                    command: valueToReferenceCommand ?? null,
                    id: valueToReference?.id ?? null,
                    typeElementPropertyId: valueToReference?.typeElementPropertyId ?? null,
                };
            }

            if (valueToReferenceRelative) {
                operationToSave.valueElementToReferenceRelativeDeveloperName =
                    valueToReferenceRelative.developerName;
                operationToSave.valueElementToReferenceRelativeContentType =
                    valueToReferenceRelative.contentType;
                operationToSave.valueElementToReferenceRelativePropertyDeveloperName =
                    valueToReferenceRelative.typeElementPropertyDeveloperName;
                operationToSave.valueElementToReferenceRelativeId = {
                    command: valueToReferenceRelativeCommand ?? null,
                    id: valueToReferenceRelative?.id ?? null,
                    typeElementPropertyId: valueToReferenceRelative?.typeElementPropertyId ?? null,
                    relativeUnit: valueToReferenceRelativeUnit,
                };
            }

            operations[index] = operationToSave;

            setMapElement({
                ...mapElement,
                operations,
            });

            onSwitchScreen(screens.operator);
        }
    };

    const onCancel = () => {
        onSwitchScreen(screens.operator);
        setSelectedOperationIndex(null);
    };

    const onChangeValueToApply = (newValue: ValueElementIdReferenceAPI | null) => {
        setValueToApply(newValue);

        if (newValue === undefined || newValue === null || newValue.id !== valueToApply?.id) {
            // Reset other form controls
            setValueToApplyCommand(null);
            setValueToReference(null);
            setValueToReferenceCommand(null);
        }
    };

    const onChangeApplyCommand: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) => {
        setValueToApplyCommand(value);
        setValueToReference(null);
        setValueToReferenceCommand(null);
    };

    const onChangeReferenceCommand: ChangeEventHandler<HTMLSelectElement> = ({
        target: { value },
    }) => {
        setValueToReferenceCommand(value);
        setValueToReference(null);
        resetReferenceRelative();
    };

    const onChangeReferenceRelativeCommand: ChangeEventHandler<HTMLSelectElement> = ({
        target: { value },
    }) => {
        setValueToReferenceRelativeCommand(value);
    };

    const onChangeValueToReference = (value: ValueElementIdReferenceAPI | null) => {
        setValueToReference(value);
        resetReferenceRelative();
    };

    const resetReferenceRelative = () => {
        setValueToReferenceRelativeCommand('');
        setValueToReferenceRelative(null);
        setValueToReferenceRelativeUnit('');
    };

    const OnChangeValueToReferenceRelativeUnit: ChangeEventHandler<HTMLSelectElement> = ({
        target: { value },
    }) => {
        setValueToReferenceRelativeUnit(value);
    };

    const onChangeSortProperty: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) => {
        const property = getByID(value, sortProperties);
        setSelectedSortProperty(property ?? null);
    };

    const onChangeSortDirection: ChangeEventHandler<HTMLSelectElement> = ({ target: { value } }) =>
        setSortDirection(value as OperationSortDirection);

    const getRelativeTimeUnits = (referenceContentType: ContentType | '' | null) => {
        if (referenceContentType === 'ContentDate') {
            return RELATIVE_DATE_UNITS;
        }
        return RELATIVE_DATETIME_UNITS;
    };

    /**
     * Computed values
     */

    // Only show the apply command selector when a value has been chosen because the options are dependant on the chosen value
    const showApplyCommandSelector = !isNullOrEmpty(valueToApply);
    // Only show sort selectors if if sort command is selected
    const showSortSelectors =
        valueToApplyCommand === OPERATION_COMMANDS.sort.command && sortProperties.length > 0;
    // Only show reference command selector when applicable apply commands are selected
    const showReferenceCommandSelector =
        valueToApplyCommand !== null &&
        (
            [
                OPERATION_COMMANDS.setEqual.command,
                OPERATION_COMMANDS.remove.command,
                OPERATION_COMMANDS.add.command,
                OPERATION_COMMANDS.divide.command,
                OPERATION_COMMANDS.multiply.command,
                OPERATION_COMMANDS.modulo.command,
                OPERATION_COMMANDS.setRelativeDate.command,
            ] as string[]
        ).includes(valueToApplyCommand);
    // Only show reference value selector when a reference command has been selected
    const showReferenceValueSelector =
        showReferenceCommandSelector && valueToReferenceCommand !== '';
    // Only show the relative settings when all other settings have been chosen
    const showRelativeValueSettings =
        !isNullOrEmpty(valueToReference) &&
        valueToApplyCommand === OPERATION_COMMANDS.setRelativeDate.command;
    // Calculate the applicable command options
    const visibleApplyCommands = getVisibleApplyCommands(valueToApply);
    // Calculate the applicable reference commands
    const visibleReferenceCommands = getVisibleReferenceCommands(
        valueToApply,
        showReferenceCommandSelector,
    );
    // Calculate the applicable relative reference commands
    const visibleReferenceRelativeCommands =
        getVisibleReferenceRelativeCommands(showRelativeValueSettings);

    // The content type of the reference value - used to filter the options in the reference value selector
    const referenceContentType = getValidReferenceContentType(
        valueToApply,
        valueToApplyCommand,
        valueToReferenceCommand,
        showReferenceValueSelector,
    );
    // Get the time units which will be used for relative operations - differs between Datetime/Date
    const relativeTimeUnits = getRelativeTimeUnits(referenceContentType);

    /**
     * Effects
     */

    // Get the reference value's type id to pass into the value selector's filter
    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        // If the command is "Length Of" (GET_LENGTH) then any list is acceptable,
        // so we can clear the referenceTypeId.
        if (
            referenceContentType === 'ContentList' &&
            valueToReferenceCommand === OPERATION_COMMANDS.getLength.command
        ) {
            setReferenceTypeId('');
            return;
        }

        const valueToApplyId = valueToApply?.id ?? null;

        (async () => {
            if (
                (referenceContentType === 'ContentList' ||
                    referenceContentType === 'ContentObject') &&
                valueToApplyId !== null
            ) {
                try {
                    const valueResponse = await getValue(valueToApplyId);

                    setReferenceTypeId(valueResponse.typeElementId);
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                }
            } else {
                setReferenceTypeId('');
            }
        })();
    }, [valueToApplyCommand, valueToReferenceCommand, referenceContentType]);

    // Get the properties of the selected value for sorting purposes
    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        (async () => {
            const valueToApplyId = valueToApply?.id ?? null;

            if (
                valueToApplyId !== null &&
                valueToApplyCommand === OPERATION_COMMANDS.sort.command
            ) {
                try {
                    const valueResponse = await getValue(valueToApplyId);

                    if (valueResponse && typeof valueResponse.typeElementId === 'string') {
                        const type = await getType(valueResponse.typeElementId);

                        if (operation?.valueElementToApplySortById) {
                            const property = getByID(
                                operation.valueElementToApplySortById,
                                type.properties ?? [],
                            );
                            setSelectedSortProperty(property ?? null);
                        }

                        setSortProperties(type.properties);
                    }

                    if (operation?.valueElementToApplySortDirection) {
                        setSortDirection(operation.valueElementToApplySortDirection);
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                }
            } else {
                // Reset sort form controls
                setSortProperties([]);
                setSortDirection('ASCENDING');
                setSelectedSortProperty(null);
            }
        })();
    }, [valueToApply, valueToApplyCommand]);

    useEffect(() => {
        setConfigTitle(`Operation for: ${mapElement.developerName}`);

        return () => setConfigTitle(null);
    }, [mapElement.developerName, setConfigTitle]);

    // Get the full value reference objects (valueToApply and valueToReference)
    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        (async () => {
            if (isLoadingValues) {
                const valueToApplyId = operation?.valueElementToApplyId?.id ?? null;
                const valueToReferenceId = operation?.valueElementToReferenceId?.id ?? null;
                const valueToReferenceRelativeId =
                    operation?.valueElementToReferenceRelativeId?.id ?? null;

                if (!(valueToApplyId || valueToReferenceId || valueToReferenceRelativeId)) {
                    setIsLoadingValues(false);
                    return;
                }

                const ids: string[] = [];
                if (valueToApplyId) {
                    ids.push(valueToApplyId);
                }
                if (valueToReferenceId) {
                    ids.push(valueToReferenceId);
                }
                if (valueToReferenceRelativeId) {
                    ids.push(valueToReferenceRelativeId);
                }

                try {
                    const responseValueReferences = await getValueList({
                        id: ids,
                    });

                    if (valueToApplyId) {
                        const propertyId =
                            operation?.valueElementToApplyId?.typeElementPropertyId ?? null;
                        const reference = responseValueReferences.find(
                            (vr) =>
                                vr.id === valueToApplyId && vr.typeElementPropertyId === propertyId,
                        );

                        setValueToApply(reference || null);
                    }
                    if (valueToReferenceId) {
                        const propertyId =
                            operation?.valueElementToReferenceId?.typeElementPropertyId ?? null;
                        const reference = responseValueReferences.find(
                            (vr) =>
                                vr.id === valueToReferenceId &&
                                vr.typeElementPropertyId === propertyId,
                        );

                        setValueToReference(reference || null);
                    }
                    if (valueToReferenceRelativeId) {
                        const propertyId =
                            operation?.valueElementToReferenceRelativeId?.typeElementPropertyId ??
                            null;
                        const reference = responseValueReferences.find(
                            (vr) =>
                                vr.id === valueToReferenceRelativeId &&
                                vr.typeElementPropertyId === propertyId,
                        );

                        setValueToReferenceRelative(reference || null);
                    }
                    setIsLoadingValues(false);
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: {
                            error,
                        },
                    });
                }
            }
        })();
    }, []);

    const renderBody = () => {
        if (isLoadingValues) {
            return <Loader />;
        }

        return (
            <>
                <FormGroup
                    label="Which Value do you want to change?"
                    isRequired
                    validationMessage="Please choose a Value"
                    isValid={valueToApply !== null}
                    showValidation={hasSubmitted}
                >
                    <ValueSelectorModal
                        value={valueToApply}
                        onChangeAsValueReference={onChangeValueToApply}
                        includeSystemValues={false}
                        container={container}
                    />
                </FormGroup>

                {showApplyCommandSelector && (
                    <FormGroup
                        label="How do you want to change the Value?"
                        isRequired
                        validationMessage="Please choose an option"
                        isValid={valueToApplyCommand !== ''}
                        showValidation={hasSubmitted}
                        htmlFor="valueToApplyCommand"
                    >
                        <select
                            className="form-control form-select form-control-width"
                            value={valueToApplyCommand ?? ''}
                            onChange={onChangeApplyCommand}
                            data-testid="select-command"
                            id="valueToApplyCommand"
                        >
                            {valueToApplyCommand === null && (
                                <option key="empty" value="">
                                    Please select
                                </option>
                            )}
                            {visibleApplyCommands.map((commandType) => (
                                <option key={commandType.command} value={commandType.command}>
                                    {commandType.displayName}
                                </option>
                            ))}
                        </select>
                    </FormGroup>
                )}

                {showSortSelectors && (
                    <>
                        <FormGroup
                            label="The property to use for this sort"
                            isRequired
                            validationMessage="Please choose a property"
                            isValid={selectedSortProperty !== null}
                            showValidation={hasSubmitted}
                        >
                            <select
                                className="form-control form-select form-control-width"
                                value={selectedSortProperty?.id ?? ''}
                                onChange={onChangeSortProperty}
                            >
                                {selectedSortProperty === null && (
                                    <option key="empty" value="">
                                        Please select
                                    </option>
                                )}
                                {sortProperties.map((property) => (
                                    <option key={property.id} value={property.id}>
                                        {property.developerName}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>
                        <FormGroup label="Sort direction">
                            <select
                                className="form-control form-select form-control-width"
                                value={sortDirection}
                                onChange={onChangeSortDirection}
                            >
                                <option value="ASCENDING">Ascending</option>
                                <option value="DESCENDING">Descending</option>
                            </select>
                        </FormGroup>
                    </>
                )}

                {showReferenceCommandSelector && (
                    <FormGroup
                        label="How do you want to retrieve the reference Value?"
                        isRequired
                        validationMessage="Please choose an option"
                        isValid={valueToReferenceCommand !== ''}
                        showValidation={hasSubmitted}
                        htmlFor="valueToReferenceSelect"
                    >
                        <select
                            id="valueToReferenceSelect"
                            className="form-control form-select form-control-width"
                            value={valueToReferenceCommand ?? ''}
                            onChange={onChangeReferenceCommand}
                        >
                            {valueToReferenceCommand === null && (
                                <option key="empty" value="">
                                    Please select
                                </option>
                            )}
                            {visibleReferenceCommands.map((commandType) => (
                                <option key={commandType.command} value={commandType.command}>
                                    {commandType.displayName}
                                </option>
                            ))}
                        </select>
                    </FormGroup>
                )}

                {showReferenceValueSelector && (
                    <FormGroup
                        label="Which Value do you want to reference?"
                        isRequired
                        validationMessage="Please choose a Value"
                        isValid={valueToReference !== null}
                        showValidation={hasSubmitted}
                    >
                        <ValueSelectorModal
                            value={valueToReference}
                            onChangeAsValueReference={onChangeValueToReference}
                            includeSystemValues={true}
                            container={container}
                            contentType={referenceContentType}
                            typeElementId={referenceTypeId}
                        />
                    </FormGroup>
                )}
                {showRelativeValueSettings && (
                    <>
                        <FormGroup
                            label="What relative operation do you want to perform?"
                            isRequired
                            validationMessage="Please choose an option"
                            isValid={valueToReferenceRelativeCommand !== ''}
                            showValidation={hasSubmitted}
                            htmlFor="valueToReferenceRelativeCommand"
                        >
                            <select
                                id="valueToReferenceRelativeCommand"
                                className="form-control form-select form-control-width"
                                value={valueToReferenceRelativeCommand ?? ''}
                                onChange={onChangeReferenceRelativeCommand}
                            >
                                {valueToReferenceRelativeCommand === null && (
                                    <option key="empty" value="">
                                        Please select
                                    </option>
                                )}
                                {visibleReferenceRelativeCommands.map((commandType) => (
                                    <option key={commandType.command} value={commandType.command}>
                                        {commandType.displayName}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>
                        <FormGroup
                            label="Which Value do you want to use for the relative unit?"
                            isRequired
                            validationMessage="Please choose a Value"
                            isValid={valueToReferenceRelative !== null}
                            showValidation={hasSubmitted}
                        >
                            <ValueSelectorModal
                                value={valueToReferenceRelative}
                                onChangeAsValueReference={setValueToReferenceRelative}
                                includeSystemValues={false}
                                container={container}
                                contentType={'ContentNumber'}
                            />
                        </FormGroup>
                        <FormGroup
                            label="What unit is this value?"
                            isRequired
                            validationMessage="Please choose an option"
                            isValid={valueToReferenceRelativeUnit !== ''}
                            showValidation={hasSubmitted}
                            htmlFor="valueToReferenceRelativeUnit"
                        >
                            <select
                                id="valueToReferenceRelativeUnit"
                                className="form-control form-select form-control-width"
                                value={valueToReferenceRelativeUnit ?? ''}
                                onChange={OnChangeValueToReferenceRelativeUnit}
                            >
                                {valueToReferenceRelativeUnit === null && (
                                    <option key="empty" value="">
                                        Please select
                                    </option>
                                )}
                                {relativeTimeUnits.map((unit) => (
                                    <option key={unit} value={unit}>
                                        {unit}
                                    </option>
                                ))}
                            </select>
                        </FormGroup>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <ModalBody>{renderBody()}</ModalBody>
            <ModalFooter>
                <ButtonDefault className="flex-child-right" onClick={onCancel}>
                    Cancel
                </ButtonDefault>
                <ButtonPrimary className="margin-left" onClick={onSave}>
                    {selectedOperationIndex === null ? 'Add' : 'Update'}
                </ButtonPrimary>
            </ModalFooter>
        </>
    );
};

export default OperationDetails;
